import React, { useRef, useState, useMemo, useCallback } from "react";
import { useCandidates } from "../../contexts/CandidateContext";
import { useCampaigns } from "../../contexts/CampaignsContext";
import Modal from "../../components/UI/Modal";
import CandidateForm from "../../components/Form/CandidateForm";
import ReusableTable from "../../components/UI/ReusableTable";
import DeleteButton from "../../components/UI/AlertDialog";
import Notification from "../../components/UI/Notification";
import ReactPaginate from 'react-paginate';
import { MoreVertical, AlertTriangle } from 'lucide-react';
import { createPortal } from 'react-dom';
import StatusBadge from '../../components/UI/StatusBadge';

const Candidates = React.memo(() => {
  const { pagination, candidates, fetchCandidates, addCandidate, updateCandidate, deleteCandidate, loading } = useCandidates();
  const { campaigns } = useCampaigns();
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const buttonRefs = useRef({});
  const [dropdownPosition, setDropdownPosition] = useState('bottom');
  const [dropdownCoords, setDropdownCoords] = useState({ x: 0, y: 0 });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCandidate, setEditingCandidate] = useState(null);
  const [message, setMessage] = useState(""); // Success message
  const [formErrors, setFormErrors] = useState({}); // Validation errors
  const [notification, setNotification] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [isFiltering, setIsFiltering] = useState(false);

  const handleDropdownToggle = (requestId, event) => {
    event.stopPropagation();
    if (openDropdownId === requestId) {
      setOpenDropdownId(null);
    } else {
      setOpenDropdownId(requestId);
      // Calculate portal coordinates
      const button = event.currentTarget;
      const rect = button.getBoundingClientRect();
      const dropdownWidth = 200;
      const dropdownHeight = 180;
      let x = rect.right - dropdownWidth;
      let y = rect.bottom + 4;
      if (x < 0) x = rect.left - dropdownWidth;
      if (y + dropdownHeight > window.innerHeight) {
        y = rect.top - dropdownHeight - 4;
        setDropdownPosition('top');
      } else {
        setDropdownPosition('bottom');
      }
      if (x < 0) x = 8;
      if (y < 0) y = 8;
      setDropdownCoords({ x, y });
    }
  };

  // Handlers
  const openModalForAdd = useCallback(() => {
    setEditingCandidate(null);
    setIsModalOpen(true);
    setMessage("");
    setFormErrors({});
  }, []);

  const openModalForEdit = useCallback((candidate) => {
    setEditingCandidate(candidate);
    setIsModalOpen(true);
    setMessage("");
    setFormErrors({});
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setEditingCandidate(null);
    setFormErrors({});
    setMessage("");
  }, []);

  const convertFormDataToObject = (formData) => {
    const entries = formData.entries();
    const result = {};
    
    for (const [key, value] of entries) {
      if (value !== null && value !== undefined && value !== '' && 
          !(typeof value === 'object' && Object.keys(value).length === 0)) {
        result[key] = value;
      }
    }
    
    return result;
  };

  const handleFormSubmit = useCallback(
    async (candidate) => {
      try {
        if (editingCandidate) {

          // Convert FormData to object
          const candidateData = candidate instanceof FormData ? convertFormDataToObject(candidate) : candidate;

          await updateCandidate({ ...editingCandidate, ...candidateData });
          setMessage("Candidate updated successfully.");
          // setNotification("Candidate updated successfully.");
          setNotification({
            message: "Candidate updated successfully.",
            type: "success",
          });
        } else {
          // console.log("create candidate", candidate)
          await addCandidate(candidate);
          setMessage("Candidate added successfully.");
          // setNotification("Candidate added successfully.");
          setNotification({
            message: "Candidate added successfully.",
            type: "success",
          });
        }
        closeModal();
      } catch (err) {
        if (err.errors) {
          setFormErrors(err.errors);
          // setNotification(err.errors);
          setNotification({
            message: err.errors,
            type: "error",
          });
        } else {
          console.error("Unexpected error during submission:", err);
          // setNotification("Unexpected error during submission:", err);
          setNotification({
            message: `Unexpected error during submission:, ${err}`,
            type: "error",
          });
        }
      }
    },
    [editingCandidate, addCandidate, updateCandidate, closeModal]
  );

  // Memoize campaigns for dropdown options
  const campaignOptions = useMemo(
    () => campaigns.map((c) => ({ value: c.id, label: c.campaign_name })),
    [campaigns]
  );

  // Memoize candidates with campaign names for the table
  const candidatesWithCampaigns = useMemo(
    () =>
      candidates.map((candidate) => ({
        ...candidate,
        campaign_name: candidate.campaign?.campaign_name || "Unknown Campaign",
      })),
    [candidates]
  );

  const handleDeleteCandidate = async (id) => {
    try {
      await deleteCandidate(id);
      // setNotification("Campaign deleted successfully");
      setNotification({
        message: "Campaign deleted successfully",
        type: "success",
      });
    } catch (error) {
      // setNotification("Failed to delete campaign");
      setNotification({
        message: "Failed to delete campaign",
        type: "error",
      });
    }
  };

  // Table columns
  const getColumns = useMemo(() => {
    return (currentPage, pageSize) => [
      {
        label: "Sr. No",
        field: "sr_no",
        render: (_row, idx) => (currentPage - 1) * pageSize + idx + 1,
      },
      { label: "Name", field: "name" },
      { label: "Email", field: "email" },
      {
        label: "Campaign Name",
        field: "campaign_id",
        render: (row) => row.campaign?.campaign_name || "-", // Access campaign_name from nested campaign
      },
      { label: "Status", field: "status",
        render: (row) => (
          <StatusBadge status={row.status} />
        )
       },
      {
        label: "Actions",
        field: "actions",
        render: (row) => (
          <div className="flex items-center justify-center">
            <div className="relative dropdown-container">
              <button
                ref={el => { if (el) buttonRefs.current[row.id] = el; }}
                onClick={e => handleDropdownToggle(row.id, e)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
              >
                <MoreVertical className="w-4 h-4 text-gray-600 dark:text-gray-400" />
              </button>
            </div>
            {openDropdownId === row.id && createPortal(
              <div
                style={{
                  position: 'fixed',
                  top: dropdownCoords.y,
                  left: dropdownCoords.x,
                  zIndex: 9999,
                }}
                className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl min-w-[150px]"
              >
                <div className=" flex flex-col space-y-0">
                  {/* Edit */}
                  <button
                    onClick={() => {openModalForEdit(row); setOpenDropdownId(null);}}
                    className="text-blue-600 hover:text-blue-800 w-full text-left px-3 py-2 text-sm hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors block"
                  >
                    Edit
                  </button>
                  {/* Delete */}
                  <DeleteButton style={`text-left text-red-600 text-sm hover:text-red-800 px-3 py-2`} onDelete={() => {handleDeleteCandidate(row.id); setOpenDropdownId(null);}} />
                </div>
              </div>,
              document.body
            )}
        </div>
        ),
      },
    ];
  }, [openModalForEdit, openDropdownId, dropdownCoords, deleteCandidate]);


  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setCurrentPage(newPage);
      fetchCandidates(newPage);
    }
  };


  return (
    <div className="px-3 py-2 bg-white dark:bg-gray-900 font-poppins rounded-lg">

      {/* notification compo */}
      {notification && (
        <Notification message={notification.message} type={notification.type} onClose={() => setNotification(null)} />
      )}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <h2 className="text-2xl font-semibold text-gray-800 dark:text-white">Candidates</h2>
        <button
          onClick={openModalForAdd}
          className="bg-blue-600 text-white font-medium px-4 py-2 rounded-md hover:bg-blue-700 w-full sm:w-auto"
        >
          Add Candidate
        </button>
      </div>

      {/* Candidates Table */}
      <div>
        <ReusableTable
          data={candidates}
          columns={getColumns(currentPage, pagination.perPage)}
          filterable={true}
          loading={loading}
          excludeFilterColumns={["resume_path", "actions"]}
          onFilterChange={(filtering) => setIsFiltering(filtering)}
        />

        {/* Pagination using React Paginate */}
        {!isFiltering && candidates?.length > 0 && (
          <ReactPaginate
            breakLabel="..."
            nextLabel="Next >"
            onPageChange={(event) => handlePageChange(event.selected + 1)}
            forcePage={currentPage - 1}
            pageRangeDisplayed={5}
            disabledClassName='opacity-50 cursor-not-allowed'
            disabledLinkClassName='pointer-events-none'
            pageCount={pagination.totalPages}
            previousLabel="< Prev"
            previousClassName='dark:text-white px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-100 dark:border-gray-600 dark:hover:bg-gray-800'
            nextClassName="dark:text-white px-3 py-1 border border-gray-300 rounded-md hover:bg-gray-100 dark:border-gray-600 dark:hover:bg-gray-800"
            renderOnZeroPageCount={null}
            containerClassName="flex items-center justify-center mt-4 font-poppins"
            pageClassName="mx-1 px-3 py-1 border rounded dark:text-white font-poppins"
            activeClassName="bg-blue-500 text-white dark:bg-black dark:text-white dark:border-gray-700 font-poppins"
            />
        )}
      </div>

      {/* Modal for Adding or Editing */}
      <Modal
        isOpen={isModalOpen}
        onRequestClose={closeModal}
        title={editingCandidate ? "Edit Candidate" : "Add Candidate"}>
        <CandidateForm
          loading={loading}
          closeModal={closeModal}
          onSubmit={handleFormSubmit}
          campaignOptions={campaignOptions}
          initialValues={editingCandidate || { campaign_id: "", name: "", email: "", status: "" }}
          errors={formErrors}
          notification={notification}
          setNotification={setNotification} />
      </Modal>
    </div>
  );
});

export default Candidates;
